// #2816 (scribe-resilience G3) — pure helpers behind the contender-compare
// dialog, split out of the component so they're unit-testable without
// mounting React (same posture RideEditor.jsx's own sortSheetsByDisplayPosition
// gets, see RideEditor.judgeColumnOrder.test.js).
import { isAuthoritativeScoreSheet } from "score-common";

// Groups a ride's full sheet list (RideEditor's `sheets` state — every
// sheet, contenders included) by judgeId (the seat). A seat with only one
// sheet has no contender; a seat with 2+ does. Sheets within a group are NOT
// reordered here — callers that need the authoritative one first (or last)
// do that themselves, since the two current callers want different orders
// (the tab strip wants authoritative-only; the compare dialog wants
// authoritative first, for a stable "left column is the current truth"
// layout).
export function groupSheetsBySeat(sheets) {
  const groups = new Map();
  for (const sheet of sheets ?? []) {
    const key = sheet.judgeId ?? sheet.id;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(sheet);
  }
  return groups;
}

// Only the authoritative sheet per seat — what RideEditor's tabs/summary
// list/editor should ever read or write, so a scribe or admin can never
// open, save, confirm, or complete a CONTENDER sheet by tabbing to it. Order
// is preserved from the input array (RideEditor already runs
// sortSheetsByDisplayPosition over the full list before this is applied).
export function authoritativeSheetsOnly(sheets) {
  return (sheets ?? []).filter(isAuthoritativeScoreSheet);
}

// The seats that currently have a contender (2+ sheets), as an array of
// { judgeId, sheets } — sheets ordered authoritative-first so a caller
// rendering "sheet A (authoritative) vs sheet B" never has to re-check which
// one currently holds the seat.
export function contenderSeats(sheets) {
  const groups = groupSheetsBySeat(sheets);
  const result = [];
  for (const [judgeId, group] of groups) {
    if (group.length < 2) continue;
    const ordered = [...group].sort((a, b) => {
      const aAuth = isAuthoritativeScoreSheet(a) ? 0 : 1;
      const bAuth = isAuthoritativeScoreSheet(b) ? 0 : 1;
      if (aAuth !== bAuth) return aAuth - bAuth;
      // Stable secondary order for two non-authoritative sheets (shouldn't
      // happen under the one-authoritative-per-seat invariant, but never
      // throw or silently drop one if it somehow does): oldest first.
      return (a.createdAt ?? "").localeCompare(b.createdAt ?? "");
    });
    result.push({ judgeId, sheets: ordered });
  }
  return result;
}

// Union of movement numbers across every sheet in the seat, each row
// carrying that movement's mark from EVERY sheet plus whether they differ
// (AC1 — "per-movement differences visibly marked"). A movement present on
// only one sheet still gets a row (the other column reads as absent, not
// silently dropped) — same posture SheetAuditDialog's own
// movementReconciliation established for its as-confirmed/as-signed/current
// columns.
//
// #2818 (scribe-resilience G7, AC2 judge-deny-1 item 3): `differs` used to
// look at `.score` only. A movement can also differ by its deductions,
// flagged status, or per-movement remarks without its score changing --
// mirrors classifyMutation's marksChanged/remarksChanged (apps/score-api/
// src/lib/audit.js) -- so flag those too. The row shape (num/marks/differs)
// is unchanged, so this only makes `differs` true in more cases; it never
// regresses a caller (ContenderCompareDialog included) that only reads
// mark.score.
//
// #2986: that widening created a gap ContenderCompareDialog fell into --
// `differs` can now go true from a field the dialog never rendered
// (deductions/flagged/remarks/annotated*), highlighting a row with two
// identical-looking score cells and no visible reason. `differingFields`
// names exactly which sub-field(s) diverged (score included, when it does)
// so a caller can show the reader what it saw, not just that it saw
// something. Additive to the row shape -- a caller that only reads
// `.differs`/`.marks` (SheetAuditDialog's version-compare table) is
// unaffected.
export function buildMovementDiffRows(sheets) {
  const numbers = new Set();
  for (const sheet of sheets ?? []) {
    for (const m of sheet.movementScores ?? []) numbers.add(m.movementNumber);
  }
  return [...numbers]
    .sort((a, b) => a - b)
    .map((num) => {
      const marks = (sheets ?? []).map(
        (sheet) => sheet.movementScores?.find((m) => m.movementNumber === num) ?? null
      );
      const scores = marks.map((m) => m?.score ?? null);
      const scoreDiffers = new Set(scores.map((s) => (s === null ? "\u0000null" : s))).size > 1;

      const fieldDiffers = (getter) =>
        new Set(marks.map((m) => JSON.stringify(getter(m)))).size > 1;
      const deductionsDiffer = fieldDiffers((m) => m?.deductions ?? []);
      const flaggedDiffers = fieldDiffers((m) => !!m?.flagged);
      const remarksDiffer = fieldDiffers((m) => m?.remarks ?? "");
      const annotatedDirectiveDiffers = fieldDiffers((m) => m?.annotatedDirective ?? "");
      const annotatedDescriptionDiffers = fieldDiffers((m) => m?.annotatedDescription ?? "");
      const otherFieldsDiffer =
        deductionsDiffer ||
        flaggedDiffers ||
        remarksDiffer ||
        annotatedDirectiveDiffers ||
        annotatedDescriptionDiffers;

      const differingFields = [
        ...(scoreDiffers ? ["score"] : []),
        ...(deductionsDiffer ? ["deductions"] : []),
        ...(flaggedDiffers ? ["flagged"] : []),
        ...(remarksDiffer ? ["remarks"] : []),
        ...(annotatedDirectiveDiffers ? ["annotatedDirective"] : []),
        ...(annotatedDescriptionDiffers ? ["annotatedDescription"] : []),
      ];

      return { num, marks, differs: scoreDiffers || otherFieldsDiffer, differingFields };
    });
}

// #2818 (scribe-resilience G7, AC2 judge-deny-1 item 3): the sheet-level
// fields buildMovementDiffRows can't represent because they aren't attached
// to any single movement number -- collectives/eliminations/errors/totals
// (score-api's classifyMutation tags these EXCEPTIONAL) plus the sheet-level
// remarks fields (generalRemarks/annotatedPurpose/annotatedIntroduce,
// tagged NORMAL). Mirrors apps/score-api/src/lib/audit.js's
// NUMERIC_SHEET_FIELDS/NORMAL_SHEET_FIELDS field NAMES (not imported --
// score-api and scoreadmin-fe are separate deployables) -- keep this list in
// sync if that one changes. Only fields that actually differ are returned,
// so an unchanged compare shows no noise rows.
const SHEET_LEVEL_DIFF_FIELDS = [
  "collectives",
  "eliminations",
  "errors",
  "totalPoints",
  "maxPoints",
  "percentage",
  "technicalPoints",
  "technicalMaxPoints",
  "artisticPoints",
  "artisticMaxPoints",
  "qualityPoints",
  "qualityMaxPoints",
  "generalRemarks",
  "annotatedPurpose",
  "annotatedIntroduce",
];

// #2987 -- the three sheet-level remark fields are only ambiguous with a
// genuinely-recorded empty value at a version BEFORE the sheet's first
// sign/remark-changeset (score-api's lib/audit.js emptyVersionContentState).
// A version's snapshot from GET /ride/:rideId/audit carries a matching
// `<field>Known` flag for exactly these three (score-api's
// KNOWABLE_REMARK_FIELDS); `current`/`baselines.confirmed`/`.signed` snapshots
// (buildConfirmedSnapshot/buildSignedSnapshot) never set it at all, so its
// absence here means "not a reconstructed version, treat as known" -- only an
// explicit `false` means "never recorded, not comparable".
const NOT_ALWAYS_KNOWN_FIELDS = new Set(["generalRemarks", "annotatedPurpose", "annotatedIntroduce"]);

export function buildSheetLevelDiffRows(sheets) {
  const list = sheets ?? [];
  const rows = [];
  for (const field of SHEET_LEVEL_DIFF_FIELDS) {
    if (NOT_ALWAYS_KNOWN_FIELDS.has(field) && list.some((s) => s?.[`${field}Known`] === false)) {
      // At least one side never recorded this field -- not a real
      // difference, just this reconstruction's own placeholder default.
      continue;
    }
    const values = list.map((s) => s?.[field] ?? null);
    const serialized = values.map((v) => JSON.stringify(v));
    if (new Set(serialized).size > 1) {
      rows.push({ field, values });
    }
  }
  return rows;
}
